import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/widgets/layout/ui/AppLayout'
import { SkeletonStack } from '@/shared/ui'
import { CallRow, CallRowHead, useCalls, useOutcomes } from '@/entities/call'
import { useBots } from '@/entities/bot'
import type { Call } from '@/shared/api/types'
import styles from './CallsPage.module.css'

/** Быстрые срезы. Покрывают почти все вопросы к списку. */
type Slice = 'all' | 'good' | 'bad'

export function CallsPage() {
  const [slice, setSlice] = useState<Slice>('all')
  const [outcome, setOutcome] = useState('')
  const [query, setQuery] = useState('')
  const [bot, setBot] = useState('')
  const bots = useBots()
  const botNames = bots.data?.names ?? {}
  const hasBots = Object.keys(botNames).length > 0

  const calls = useCalls({ outcome: outcome || undefined, bot: bot || undefined })
  const outcomes = useOutcomes()

  // Отбор по срезу и поиску идёт на клиенте: список за раз приходит
  // целиком, и гонять запрос на каждую букву незачем.
  const found = useMemo(() => {
    const needle = query.trim().toLowerCase()

    return (calls.data ?? []).filter((call) => {
      if (slice === 'good' && !call.report?.success) return false
      if (slice === 'bad' && !isBad(call)) return false

      if (!needle) return true
      const haystack = `${call.report?.outcomeLabel ?? ''} ${call.report?.summary ?? ''}`
      return haystack.toLowerCase().includes(needle)
    })
  }, [calls.data, slice, query])

  const days = useMemo(() => groupByDay(found), [found])

  return (
    <>
      <PageHeader title="Звонки" />

      <div className={styles.panel}>
        <div className={styles.toolbar}>
          <div className={styles.filters}>
            {(
              [
                ['all', 'Все'],
                ['good', 'К делу'],
                ['bad', 'Отказы'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`${styles.chip} ${slice === value ? styles.chipActive : ''}`}
                onClick={() => setSlice(value)}
              >
                {label}
              </button>
            ))}
          </div>

          <select
            className={styles.select}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            aria-label="Итог разговора"
          >
            <option value="">Любой итог</option>
            {outcomes.data?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {hasBots && (
            <select
              className={styles.select}
              value={bot}
              onChange={(e) => setBot(e.target.value)}
              aria-label="Бот"
            >
              <option value="">Любой бот</option>
              <option value="main">Основной бот</option>
              {Object.entries(botNames)
                .sort((a, b) => Number(a[0]) - Number(b[0]))
                .map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
            </select>
          )}

          <input
            className={styles.search}
            placeholder="Найти по итогу или сводке…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <span className={styles.count}>{found.length}</span>
        </div>

        {calls.isPending ? (
          <div style={{ padding: 'var(--space-4)' }}>
            <SkeletonStack rows={5} height={40} />
          </div>
        ) : found.length ? (
          <>
            <CallRowHead />

            {days.map(([day, items]) => (
              <div key={day}>
                <div className={styles.day}>
                  {day}
                  <span className={styles.dayCount}>
                    {items.length} · {minutes(items)} мин
                  </span>
                </div>
                {items.map((call) => (
                  <CallRow key={call.id} call={call} botName={call.botId ? botNames[String(call.botId)] : undefined} />
                ))}
              </div>
            ))}
          </>
        ) : (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>
              {calls.data?.length ? 'Ничего не нашлось' : 'Звонков пока не было'}
            </p>
            <p className={styles.emptyHint}>
              {calls.data?.length ? (
                'Попробуйте другой срез или очистите поиск.'
              ) : (
                <>
                  Начните с проверки:{' '}
                  <Link className={styles.link} to="/test-call">
                    позвоните агенту сами
                  </Link>{' '}
                  и послушайте, как он говорит.
                </>
              )}
            </p>
          </div>
        )}
      </div>
    </>
  )
}

/** Разговор кончился ничем и продолжения не будет. */
function isBad(call: Call): boolean {
  const outcome = call.report?.outcome
  return outcome === 'refusal' || outcome === 'hangup' || outcome === 'no_interest'
}

/**
 * Разбивка по дням.
 *
 * Разговоры смотрят по дням — «что было вчера», — и без заголовков
 * двадцать строк выглядят одной кучей. Порядок сохраняется: список уже
 * пришёл свежими вперёд.
 */
function groupByDay(calls: Call[]): [string, Call[]][] {
  const groups = new Map<string, Call[]>()

  for (const call of calls) {
    const key = dayLabel(call.startedAt ?? call.createdAt)
    const list = groups.get(key)
    if (list) list.push(call)
    else groups.set(key, [call])
  }

  return [...groups.entries()]
}

function dayLabel(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 'Когда-то'

  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  if (sameDay(date, today)) return 'Сегодня'
  if (sameDay(date, yesterday)) return 'Вчера'

  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  )
}

function minutes(calls: Call[]): number {
  return Math.round(calls.reduce((sum, call) => sum + call.durationMs, 0) / 60000)
}




